import { createRef, useState, useEffect, FunctionComponent, useCallback, MouseEvent } from 'react';

// Components
import Contact from '../Contact';
import Support from '../Support';
import Question from '../Question';
import Cooperate from '../Cooperate';
import Anchor from '@Components/Utils/Anchor';
import ImgLazy from '@Components/Utils/Lazy/Image';
import AvatarStack from '@Components/UI/AvatarStack';
import ButtonTawkTo from '@Components/UI/ButtonTawkTo';
import { Dropdown, DropdownToggle, DropdownMenu } from 'reactstrap';
import { useViewDetected, ViewMedia } from '@Components/Utils/ViewDetected';

// Styles
import styles from './Access.module.scss';

// Utilities
import classnames from 'classnames';

const OPTIONS = [
    {
        link: '',
        tab: 'cooperate',
        title: 'Liên hệ hợp tác',
        icon: '/assets/bottomAction/handshake.png',
    },
    {
        tab: '',
        link: '',
        isSoonLink: true,
        title: 'Trung tâm hỗ trợ',
        icon: '/assets/bottomAction/question.png',
    },
    {
        link: '',
        tab: 'support',
        title: 'Góp ý - Báo lỗi',
        icon: '/assets/bottomAction/letter.png',
    },
];

interface AccessProps {
    className?: string;
}

const Access: FunctionComponent<AccessProps> = ({ className }) => {
    const { deviceType } = useViewDetected();
    const isMobile = deviceType === 'mobile';

    const dropdownWrapperRef = createRef<HTMLDivElement>();

    const [tab, setTab] = useState<string>('');
    const [isActive, setIsActive] = useState<boolean>(false);

    const chooseTab = (tab: string) => {
        setTab(tab);
        setIsActive(true);
    };

    const goBack = useCallback(() => {
        setIsActive(false);
        setTimeout(() => {
            setTab('');
        }, 350);
    }, []);

    const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);

    const toggle = (event: any) => {
        if (
            dropdownOpen &&
            (event.target === dropdownWrapperRef.current || !dropdownWrapperRef.current.contains(event.target))
        ) {
            return;
        }

        setDropdownOpen((prevState) => !prevState);
        goBack();
    };

    useEffect(() => {
        if (deviceType === 'mobile') {
            if (dropdownOpen) {
                document.body.classList.add('overflow-hidden');
                // document.querySelector('html').classList.add('overflow-hidden');
            } else {
                document.body.classList.remove('overflow-hidden');
                // document.querySelector('html').classList.remove('overflow-hidden');
            }
        }
    }, [dropdownOpen]);

    const openTawkTo = (event: MouseEvent<HTMLElement>) => {
        toggle(event);
    };

    const renderOption = () => {
        return OPTIONS.map((item, index) => {
            if (item.isSoonLink) {
                return (
                    <div key={`option-${index}`} className={styles.option}>
                        <ImgLazy src={item.icon} width={24} height={24} />
                        {item.title}
                        <div className={styles.soon_link}>Sắp ra mắt</div>
                    </div>
                );
            }

            if (item.tab) {
                return (
                    <div key={`option-${index}`} className={styles.option} onClick={() => chooseTab(item.tab)}>
                        <ImgLazy src={item.icon} width={24} height={24} />
                        {item.title}
                    </div>
                );
            }

            return (
                <Anchor href={item.link} key={`option-${index}`} className={styles.option} onClick={toggle}>
                    <ImgLazy src={item.icon} width={24} height={24} />
                    {item.title}
                </Anchor>
            );
        });
    };

    const renderTab = useCallback(() => {
        switch (tab) {
            case 'cooperate':
                return <Cooperate onBack={goBack} />;
            case 'support':
                return <Support onBack={goBack} />;
            case 'question':
                return <Question onBack={goBack} />;
            default:
                return null;
        }
    }, [tab]);

    const AccessWrapperClassName = classnames([styles.wrapper, styles[deviceType], className]);

    return (
        <div ref={dropdownWrapperRef}>
            <Dropdown isOpen={dropdownOpen} direction={'up'} className={AccessWrapperClassName} toggle={toggle}>
                <DropdownToggle className={classnames([styles.icon_wrapper])} tag="div">
                    <ImgLazy
                        className={classnames([styles.icon])}
                        src={`/assets/bottomAction/${dropdownOpen ? 'down' : 'suggest'}.png`}
                        width={'56px'}
                        height={'56px'}
                    />
                </DropdownToggle>
                <DropdownMenu right={!isMobile} className={classnames([styles.access_menu, styles[deviceType]])}>
                    <div
                        className={classnames([
                            styles.main,
                            {
                                [styles.choose]: isActive,
                            },
                        ])}
                    >
                        <div className={styles.tab_wrapper}>
                            <div className={styles.tabs}>
                                <ViewMedia is={'mobile'}>
                                    <span className={styles.close} onClick={toggle}>
                                        <ImgLazy src={'/assets/bottomAction/close.png'} width={16} height={16} />
                                    </span>
                                </ViewMedia>

                                <div className={styles.hello}>Xin chào!</div>

                                <div className={styles.title}>Bạn cần hỗ trợ thông tin?</div>

                                <div className={styles.subtitle}>
                                    Trò chuyện ngay với nhân viên tư vấn eDoctor về dịch vụ chăm sóc sức khỏe nhé!
                                </div>

                                <ButtonTawkTo className={styles.message} onClick={openTawkTo}>
                                    Chat ngay với tư vấn viên
                                </ButtonTawkTo>

                                <div className={styles.question}>
                                    <div className={styles.name}>
                                        <span>Bạn có vấn đề về sức khỏe cần Bác sĩ tư vấn?</span>
                                    </div>
                                    <AvatarStack />
                                    <button className={styles.btn} onClick={() => chooseTab('question')}>
                                        Đặt câu hỏi
                                    </button>
                                    <div className={styles.tip}>
                                        <span>Đặt ngay câu hỏi để nhận tư vấn từ đội ngũ Bác sĩ bạn nhé!</span>
                                    </div>
                                </div>

                                <div className={styles.options}>{renderOption()}</div>

                                <Contact />
                            </div>
                        </div>

                        <div className={styles.tab}>{renderTab()}</div>
                    </div>
                </DropdownMenu>
            </Dropdown>
        </div>
    );
};

export default Access;
